/* eslint-disable no-console */
import { getLogLevel, isDebugEnabled } from '../config/config'

type LogLevel = 'debug' | 'info' | 'warn' | 'error'

class Logger {
  private readonly logLevel: LogLevel

  constructor() {
    this.logLevel = getLogLevel() as LogLevel
  }

  debug(message: string, ...args: unknown[]): void {
    if (this.shouldLog('debug') && isDebugEnabled()) {
      console.debug(this.formatMessage('debug', message), ...args)
    }
  }

  info(message: string, ...args: unknown[]): void {
    if (this.shouldLog('info')) {
      console.info(this.formatMessage('info', message), ...args)
    }
  }

  warn(message: string, ...args: unknown[]): void {
    if (this.shouldLog('warn')) {
      console.warn(this.formatMessage('warn', message), ...args)
    }
  }

  error(message: string, ...args: unknown[]): void {
    if (this.shouldLog('error')) {
      console.error(this.formatMessage('error', message), ...args)
    }
  }

  group(label: string): void {
    if (isDebugEnabled()) {
      console.group(label)
    }
  }

  groupEnd(): void {
    if (isDebugEnabled()) {
      console.groupEnd()
    }
  }

  table(data: unknown): void {
    if (isDebugEnabled()) {
      console.table(data)
    }
  }

  time(label: string): void {
    if (isDebugEnabled()) {
      console.time(label)
    }
  }

  timeEnd(label: string): void {
    if (isDebugEnabled()) {
      console.timeEnd(label)
    }
  }

  private shouldLog(level: LogLevel): boolean {
    const levels: Record<LogLevel, number> = {
      debug: 0,
      info: 1,
      warn: 2,
      error: 3,
    }

    return levels[level] >= levels[this.logLevel]
  }

  private formatMessage(level: LogLevel, message: string): string {
    const timestamp = new Date().toISOString()
    const prefix = `[${timestamp}] [${level.toUpperCase()}]`

    return `${prefix} ${message}`
  }
}

// Export singleton instance
export const logger = new Logger()

// Convenience functions
export const debug = (message: string, ...args: unknown[]): void => logger.debug(message, ...args)
export const info = (message: string, ...args: unknown[]): void => logger.info(message, ...args)
export const warn = (message: string, ...args: unknown[]): void => logger.warn(message, ...args)
